/**
 * Origination: where a prospect or mandate came from and who introduced it.
 *
 * Pure. Shared by the API validation, the Source control, and every create
 * path (see shared/originationByPath.ts). The list and the per-type pointer
 * rule live here and nowhere else.
 */
export const ORIGINATION_SOURCE_TYPES = [
  "coi_referral",
  "advocate_referral",
  "buyer_intro",
  "partner_network",
  "inbound_website",
  "outbound",
  "event",
  "other",
] as const;
export type OriginationSourceType = (typeof ORIGINATION_SOURCE_TYPES)[number];

export const ORIGINATION_SOURCE_LABELS: Record<OriginationSourceType, string> = {
  coi_referral: "COI referral",
  advocate_referral: "Advocate referral",
  buyer_intro: "Buyer introduction",
  partner_network: "Partner network",
  inbound_website: "Website",
  outbound: "Outbound",
  event: "Event",
  other: "Other",
};

/** The three types whose pointer is a person. */
export function originationPointsAtContact(type: OriginationSourceType): boolean {
  return type === "coi_referral" || type === "advocate_referral" || type === "buyer_intro";
}

export interface OriginationInput {
  sourceType: OriginationSourceType;
  contactId?: string | null;
  userId?: string | null;
  submissionId?: string | null;
  outreachListId?: string | null;
  campaignId?: string | null;
  /** Event name, or free text for Other, or "research" for extension/MCP adds. */
  detail?: string | null;
  note?: string | null;
  /** YYYY-MM-DD. The introduction date. */
  occurredOn?: string | null;
  detectedFromEmailId?: string | null;
}

/** What the API returns: the input plus resolved display names. */
export interface OriginationView extends OriginationInput {
  companyId: string;
  contactName: string | null;
  contactCompanyId: string | null;
  contactCompanyName: string | null;
  userName: string | null;
  submissionHeadline: string | null;
  outreachListName: string | null;
  campaignName: string | null;
  recordedAt: string | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Null when valid, otherwise a message fit for a 400 response.
 * API-level rather than a CHECK constraint: contact_id is ON DELETE SET NULL,
 * and a CHECK would make deleting a COI fail on every company it referred.
 */
export function originationValidationError(input: Partial<OriginationInput>): string | null {
  const type = input.sourceType;
  if (!type || !(ORIGINATION_SOURCE_TYPES as readonly string[]).includes(type)) {
    return "sourceType must be one of: " + ORIGINATION_SOURCE_TYPES.join(", ");
  }
  if (originationPointsAtContact(type) && !input.contactId) {
    return `${ORIGINATION_SOURCE_LABELS[type]} needs the contact who introduced it`;
  }
  if (type === "partner_network" && !input.userId) {
    return "Partner network needs the user whose network it was";
  }
  if (input.occurredOn && !ISO_DATE.test(input.occurredOn)) {
    return "occurredOn must be a YYYY-MM-DD date";
  }
  return null;
}
